import Vue from 'vue'
import Vuex from 'vuex'
import merge from 'lodash.merge'
import Cookies from 'js-cookie'
import qs from 'qs'
import modules from './modules'
import axiosQuoteApi from '@/services/axiosQuoteApi'
import axiosRatingEngine from '@/services/axiosRatingEngine'

Vue.use(Vuex)

const getQuote = (quoteId) => {
  return axiosQuoteApi.get(`quote/${quoteId}`)
}

const newQuote = () => {
  return axiosQuoteApi.post(`quote`)
}

const updateQuoteFromAccount = (quoteId, account) => {
  return axiosQuoteApi.post(`/quote/${quoteId}/account`, {
    accountId: account.id,
    industryId: account.industry_id,
    turnover: account.annual_turnover,
    employeeCount: account.employee_count,
    isValidBusiness: account.is_valid_business,
  })
}

export default new Vuex.Store({
  modules,
  state: {
    portal: null,
    accountActiveRates: [],
    quoteId: '',
    contact: {
      businessName: '',
      businessNumber: '',
      isValidBusiness: false,
      firstName: '',
      lastName: '',
      industry: {
        value: '',
      },
      industryFromParam: false,
      isRicsMember: false,
      providesOutsourcedServices: false,
      email: '',
      phone: '',
      turnover: '',
      // jurisdictionPercentage: '',
      employeeCount: '',
      ern: '',
      ernExempt: false,
      premises: [],
      effectiveDate: new Date(Date.now()),
      piRatingType: '',
      referQuote: false,
      tradingName: '',
      oldQuoteId: {},
    },
    couponId: [],
    paymentLumpSum: false,
    reference: '',
    convertedToAccountRef: '',
    employeeCount: '',
    products: [],
    statement: [],
    chargebeeEstimate: {},
    configuredRate: null,
    activeProductSlide: 0,
    estimate: null,
    estimateLoading: false,
    referrer: '',
    source: '',
    storeReady: false,
    portalReady: false,
    builderReady: false,
    removedProducts: [],
    quoteStateUpdated: false,
    total: '',
    slides: [],
    productsContent: {},
    slidesBackground: '',
    urbanConsent: false,
    availableProducts: [],
    customerPortalToken: null,
  },
  mutations: {
    setPremises (state, payload) {
      if (state.contact.premises.value && state.contact.premises.value !== payload.value) state.quoteStateUpdated = true

      state.contact.premises = payload
    },
    updateQuoteId (state, payload) {
      state.quoteId = payload
      state._id = payload
      state.contact.quoteId = payload
    },
    updateOldQuoteId (state, oldQuoteId) {
      state.contact.oldQuoteid = oldQuoteId
    },
    setPortalData (state, portalData) {
      state.portal = portalData
    },
    setAccountActiveRates (state, rates) {
      state.accountActiveRates = rates
    },
    updateStore (state, newState) {
      Object.assign(state, newState)
      state.quoteId = newState._id
    },
    updateBusinessName (state, businessName) {
      state.contact.businessName = businessName
    },
    updateBusinessNumber (state, businessNumber) {
      state.contact.businessNumber = businessNumber
    },
    updateIsValidBusiness (state, isValidBusiness) {
      if (state.contact.isValidBusiness.value && state.contact.isValidBusiness.value !== isValidBusiness.value) { state.quoteStateUpdated = true }

      state.contact.isValidBusiness = isValidBusiness
    },
    updateFirstName (state, firstName) {
      state.contact.firstName = firstName
    },
    updateLastName (state, lastName) {
      state.contact.lastName = lastName
    },
    updateIndustry (state, industry) {
      if (state.contact.industry.value && state.contact.industry.value !== industry.value) { state.quoteStateUpdated = true }

      state.contact.industry = industry
      state.contact.industry.value = industry.id
    },
    updateIndustryFromParam (state, value) {
      state.contact.industryFromParam = value
    },
    updateAvailableProducts (state, value) {
      state.availableProducts = value

      if (state.portal) {
        state.availableProducts.forEach((product) => {
          product.rates.forEach((rate) => {
            switch (rate.key) {
              case 'AvivaCommercialCombined\\GeneralLiability':
                rate.activeOnAccount = state.accountActiveRates.includes(rate.key) || state.accountActiveRates.includes('AvivaCommercialCombined\\EmployersLiability') || state.accountActiveRates.includes('AvivaCommercialCombined\\PublicLiability')
                break
              case 'CommercialCombined\\GeneralLiability':
                rate.activeOnAccount = state.accountActiveRates.includes(rate.key) || state.accountActiveRates.includes('CommercialCombined\\EmployersLiability') || state.accountActiveRates.includes('CommercialCombined\\PublicLiability')
                break
              case 'AvivaCommercialCombined\\EmployersLiability': case 'AvivaCommercialCombined\\PublicLiability':
                rate.activeOnAccount = state.accountActiveRates.includes(rate.key) || state.accountActiveRates.includes('AvivaCommercialCombined\\GeneralLiability')
                break
              case 'CommercialCombined\\EmployersLiability': case 'CommercialCombined\\PublicLiability':
                rate.activeOnAccount = state.accountActiveRates.includes(rate.key) || state.accountActiveRates.includes('CommercialCombined\\GeneralLiability')
                break
              default:
                rate.activeOnAccount = state.accountActiveRates.includes(rate.key)
            }
          })
        })
      }
    },
    updateEmail (state, email) {
      state.contact.email = email
    },
    updatePhone (state, phone) {
      state.contact.phone = phone
    },
    updateReference (state, reference) {
      state.reference = reference
    },
    updateConvertedToAccountRef (state, convertedToAccountRef) {
      state.convertedToAccountRef = convertedToAccountRef
    },
    updateCouponId: (state, couponId) => {
      state.couponId = couponId.map(c => c.toUpperCase())
    },
    setUrbanConsent: (state, urbanConsent) => {
      state.urbanConsent = urbanConsent
    },
    updateSicCodes: (state, codes) => {
      state.contact.sicCodes = codes
    },
    addCouponId (state, couponId) {
      state.couponId.push(couponId.toUpperCase())
    },
    updatePaymentLumpSum: (state, val) => {
      state.paymentLumpSum = val
    },
    updateTurnover (state, turnover) {
      if (state.contact.turnover.value && state.contact.turnover.value !== turnover.value) { state.quoteStateUpdated = true }

      state.contact.turnover = turnover
    },
    // updateJurisdictionPercentage(state, jurisdictionPercentage) {
    //   state.contact.jurisdictionPercentage = jurisdictionPercentage
    // },
    updateEmployeeCount (state, employeeCount) {
      if (state.contact.employeeCount.value && state.contact.employeeCount.value !== employeeCount.value) { state.quoteStateUpdated = true }

      state.contact.employeeCount = employeeCount
    },
    updateErn (state, ern) {
      state.contact.ern = ern
    },
    updateErnExempt (state, ernExempt) {
      state.contact.ernExempt = ernExempt
    },
    updateEffectiveDate (state, effectiveDate) {
      state.contact.effectiveDate = effectiveDate
    },
    updatePremises (state, premises) {
      if (state.contact.premises.value && state.contact.premises.value !== premises.value) { state.quoteStateUpdated = true }

      !premises.length
        ? (state.contact.premises = [])
        : (state.contact.premises = merge(state.contact.premises, premises))
    },
    updatePiRatingType (state, piRatingType) {
      state.contact.piRatingType = piRatingType
    },
    updateIsRicsMember (state, isRicsMember) {
      state.contact.isRicsMember = isRicsMember
    },
    updateProvidesOutsourcedServices (state, providesOutsourcedServices) {
      state.contact.providesOutsourcedServices = providesOutsourcedServices
    },
    updateReferQuote (state, referQuote) {
      state.contact.referQuote = referQuote
    },
    resetActiveProductSlide (state) {
      state.activeProductSlide = 0
    },
    incrementActiveProductSlide (state) {
      state.activeProductSlide++
    },
    updateActiveProductSlide (state, activeProductSlide) {
      state.activeProductSlide = activeProductSlide
    },
    updateStatements (state, payload) {
      state.statement = payload
    },
    updateStatement (state, payload) {
      const statement = state.statement.filter(item => item.id === payload.id)[0]
      statement.agreed = payload.agreed
      payload.agreed ? delete statement.comment : (statement.comment = payload.comment)
    },
    updateChargebeeEstimate (state, estimate) {
      state.chargebeeEstimate = estimate
    },
    updateReferrer (state, referrer) {
      state.referrer = referrer
    },
    updateSource (state, source) {
      state.source = source
    },
    updateConfiguredRate (state, rate) {
      state.configuredRate = rate
    },
    updateStoreReady (state, payload) {
      state.storeReady = payload
    },
    updatePortalReady (state, payload) {
      state.portalReady = payload
    },
    updateBuilderReady (state, payload) {
      state.builderReady = payload
    },
    updateRemovedProducts (state, payload) {
      state.removedProducts = payload
    },
    updateEstimate (state, payload) {
      state.estimate = payload
    },
    updateEstimateLoading (state, flag) {
      state.estimateLoading = flag
    },
    updateQuoteStateUpdated (state, payload) {
      state.quoteStateUpdated = payload
    },
    updateTradingName (state, payload) {
      state.contact.tradingName = payload
    },
    updateTotal (state, payload) {
      state.total = payload
    },
    updateFee (state, payload) {
      state.fee = payload
    },
    updateSlides (state, payload) {
      state.slides = payload
    },
    updateProducts (state, payload) {
      state.products = payload
    },
    updateProductsContent (state, payload) {
      state.productsContent = payload
    },
    updateSlidesBackground (state, payload) {
      state.slidesBackground = payload
    },
    updateSanctionsSearchRecord (state, payload) {
      state.sanctionsSearchRecord = payload
    },
    updateCustomerPortalToken (state, payload) {
      state.customerPortalToken = payload
    },
  },
  actions: {
    initialiseStore ({ commit, state, dispatch }) {
      if (Cookies.get('dr-store')) {
        Cookies.set('dr-store', Cookies.get('dr-store'), {
          expires: 2,
        })
        const quoteId = JSON.parse(Cookies.get('dr-store')).quoteId
        return getQuote(quoteId)
          .then((quoteResponse) => {
            // state.quoteId = response.data.id
            commit('updateStore', quoteResponse.data.data)
            commit('premises/setPremises', quoteResponse.data.data.contact.premises, {
              root: true,
            })

            dispatch('updateAvailableProducts').then((response) => {
              dispatch('updateProductsContent').then((response) => {
                // Let the builder know that we're ready to rock and roll.
                commit('updateBuilderReady', true)
              })
            })
          })
          .catch(() => {
            newQuote().then((quoteResponse) => {
              // state.quoteId = response.data.id
              commit('updateQuoteId', quoteResponse.data.id)
            })
          })
      } else {
        return newQuote().then((newQuoteResponse) => {
          // state.quoteId = response.data.id
          commit('updateQuoteId', newQuoteResponse.data.id)
        })
      }
    },
    updateStore ({ commit }, payload) {
      return getQuote(payload).then((response) => {
        commit('updateStore', response.data.data)
      })
    },
    async updateProductsContent (context) {
      const { data } = await axiosRatingEngine.post('/api/products/content', {
        rates: context.getters.getAvailableRates,
        industryId: context.getters.getIndustryId,
        turnover: context.getters.getTurnover,
        employeeCount: context.getters.getEmployeeCount,
        isValidBusiness: context.getters.getIsValidBusiness,
      })

      context.commit('updateProductsContent', data)
    },
    async updateFee (context) {
      const oldFee = context.getters.getFee

      const data = {
        industryId: context.getters.getIndustryId,
        turnover: context.getters.getTurnover,
        employeeCount: context.getters.getEmployeeCount,
        isValidBusiness: context.getters.getIsValidBusiness,
        premiumTotal: context.getters.getTotal,
      }

      // Get a new fee
      const response = await axiosRatingEngine.post('fee', data)
      const fee = response.data.fee
      context.commit('updateFee', fee)

      // Store the fee on the quote if it's changed.
      if (oldFee !== fee) {
        await axiosQuoteApi.post(`quote/${context.state.quoteId}/apply-fee`, {
          fee,
        })
      }
    },
    updateIndustry (context, payload) {
      axiosRatingEngine.get(`/api/industry/${payload}`).then((response) => {
        context.commit('updateIndustry', response.data)
      })
    },
    updateAvailableProducts (context) {
      return axiosRatingEngine.get(`/api/products/?${qs.stringify({
        industryId: context.getters.getIndustryId,
        turnover: context.getters.getTurnover,
        employeeCount: context.getters.getEmployeeCount,
        isValidBusiness: +context.getters.getIsValidBusiness,
        isActive: 1,
      })}`).then((response) => {
        context.commit('updateAvailableProducts', response.data)
      })
    },
    updateStatements (context, payload) {
      context.commit('updateStatements', payload)
    },
    updateSlides (context, payload) {
      context.commit('updateSlides', payload)
    },
    updateSlidesBackground (context, payload) {
      context.commit('updateSlidesBackground', payload)
    },
    updateStoreFromPortal ({ state, commit, dispatch }) {
      const account = state.portal.account

      newQuote().then(async (response) => {
        await updateQuoteFromAccount(response.data.id, account)

        getQuote(response.data.id)
          .then((quoteResponse) => {
            commit('updateStore', quoteResponse.data.data)
            commit('premises/setPremises', quoteResponse.data.data.contact.premises, {
              root: true,
            })

            // Set active rates.
            commit('setAccountActiveRates', quoteResponse.data.data.products.flatMap(product => product.rates).map(rate => rate.key))

            dispatch('updateAvailableProducts').then((response) => {
              dispatch('updateProductsContent').then((response) => {
                // Let the portal know that we're ready to rock and roll.
                commit('updatePortalReady', true)
              })
            })
          })
      })

      if (state.portal.token) {
        commit('updateCustomerPortalToken', state.portal.token)
      }
    },
    checkSanctions (context) {
      return axiosQuoteApi
        .get(`/quote/${context.state.quoteId}/check-sanctions`)
        .then((response) => {
          context.commit(`updateSanctionsSearchRecord`, response.data)
        })
    },
    async updateQuote (context, payload) {
      if (!payload) return

      // remove products without any rates - this will happen when MPA no longer applies
      for (const i in payload.products) {
        if (!payload.products[i].rates || payload.products[i].rates.length === 0) {
          payload.products.splice(i, 1)

          continue
        }
      }

      let response = await axiosQuoteApi.post(`quote/${context.state.quoteId}/products`, payload)

      context.commit('updateProducts', response.data.products)

      // Get a new fee
      if (payload.products.length === 0) context.commit('updateFee', 0)
      else response = await context.dispatch('updateFee')
    },
  },
  getters: {
    getQuoteId: state => state.quoteId,
    getAccountActiveRates: state => state.accountActiveRates.filter(rate => !rate.includes('MinimumPremiumAdjustment')),
    getBusinessName: state => state.contact.businessName,
    getBusinessNumber: state => state.contact.businessNumber,
    getIsValidBusiness: state => state.contact.isValidBusiness || false,
    getFirstName: state => state.contact.firstName,
    getLastName: state => state.contact.lastName,
    getIndustry: state => state.contact.industry,
    getIndustryId: (state, getters) => getters.getIndustry.map(industry => industry.id),
    getIndustryName: (state, getters) => getters.getIndustry.map(industry => industry.label).join(', '),
    getIndustryFromParam: state => state.contact.industryFromParam,
    getEmail: state => state.contact.email,
    getPhone: state => state.contact.phone,
    getTurnover: state => state.contact.turnover,
    // getJurisdictionPercentage: state => state.contact.jurisdictionPercentage,
    getEmployeeCount: state => state.contact.employeeCount,
    getEffectiveDate: state => state.contact.effectiveDate,
    getErn: state => state.contact.ern,
    getErnExempt: state => state.contact.ernExempt,
    getPremises: state => state.contact.premises,
    getPiRatingType: (state, getters) => {
      const piProduct = getters.getAvailableProducts.filter(product => product.key === 'ProfessionalIndemnity')[0]

      return piProduct ? piProduct.ratingType : ''
    },
    getIsRicsMember: state => state.contact.isRicsMember,
    getProvidesOutsourcedServices: state => state.contact.providesOutsourcedServices,
    getReferQuote: state => state.contact.referQuote,
    getReference: state => (state.reference && state.reference.length > 0 ? state.reference : null),
    getConvertedToAccountRef: state => state.convertedToAccountRef,
    couponIds: state => state.couponId, // getter used to have name makes more sense
    getPaymentLumpSum: state => state.paymentLumpSum,
    urbanConsent: state => state.urbanConsent,
    shouldUrbanConsent: (state) => {
      const index = state.products.findIndex(product => product.key === 'HealthcareProfessional')
      return !!(state.couponId.includes('URBAN1MONTH') || state.couponId.includes('URBAN10')) && index >= 0
    },
    getProducts: state => state.products,
    getChargebeeEstimate: state => state.chargebeeEstimate,
    getTotal: state => state.products.reduce((acc, cur) => acc + Number(cur.total), 0),
    getIptTotal: (state) => {
      let sum = 0
      state.products.forEach((product) => {
        product.rates.forEach((rate) => {
          sum += Number(rate.ipt)
        })
      })
      return sum.toFixed(2)
    },
    getStatements: state => state.statement,
    getConfiguredRate: state => state.configuredRate,
    getReferrer: state => state.referrer,
    getSource: state => state.source,
    getStoreReady: state => state.storeReady,
    getPortalReady: state => state.portalReady,
    getBuilderReady: state => state.builderReady,
    getCustomerPortalToken: state => state.customerPortalToken,
    getRemovedProducts: state => state.removedProducts,
    getEstimate: state => state.estimate,
    getEstimateLoading: state => state.estimateLoading,
    getAvailableProducts: state => state.availableProducts,
    getHasNorthAmericanCustomers: state => state.contact.hasNorthAmericanCustomers,
    getNATurnoverPercentage: state => state.contact.NATurnoverPercentage,

    /**
     * An Array of all unoffered rates for the industry
     */
    getRatesUnoffered: (state, getters) => {
      const products = getters.getAvailableProducts

      if (!products) return []

      const unofferedRatesArray = products.reduce((acc, product) => {
        const unofferedRatesInProduct = product.rates.filter(rate => rate.isOffered === false)
        if (unofferedRatesInProduct.length) return [...acc, ...unofferedRatesInProduct]
        return acc
      }, [])

      return unofferedRatesArray.length ? unofferedRatesArray : []
    },
    getRatesOffered: (state, getters) => {
      const products = getters.getAvailableProducts

      if (!products) return []

      const offeredRatesArray = products.reduce((acc, product) => {
        const offeredRatesInProduct = product.rates.filter(rate => rate.isOffered === true)
        if (offeredRatesInProduct.length) return [...acc, ...offeredRatesInProduct]
        return acc
      }, [])

      return offeredRatesArray.length ? offeredRatesArray : []
    },
    getQuoteStateUpdated: state => state.quoteStateUpdated,
    getTradingName: state => state.contact.tradingName,
    getQuoteTotal: state => state.total + state.fee,
    getSlides: state => state.slides,
    getOldQuoteId: state => state.contact.oldQuoteId,
    getAvailableRates: (state, getters) => getters.getAvailableProducts && getters.getAvailableProducts.length ? getters.getAvailableProducts.flatMap(product => product.rates).map(rate => rate.key) : [],
    getSelectedRatesIds: (state, getters) =>
      getters.getProducts.length
        ? getters.getProducts.flatMap(product => product.rates).map(rate => rate.key).filter(rate => !rate.includes('MinimumPremiumAdjustment'))
        : [],
    getOfferedRates: (state, getters) =>
      getters.getAvailableProducts
        .flatMap(product => product.rates)
        .filter(rate => rate.isOffered)
        .map(rate => rate.key),
    getProductsContent: state => state.productsContent,
    getSanctionsSearchRecord: state => state.sanctionsSearchRecord,
    getSlidesBackground: state => state.slidesBackground,

    /**
     * Calculate the discount that is being applied by subtracting the final total
     * from the total returned by Chargebee.
     */
    getDiscount (state, getters) {
      if (state.chargebeeEstimate.total === undefined) return []

      const ret = state.chargebeeEstimate.discounts || []

      if (ret.length > 0) {
        let pennyOff = getters.getTotal * 100 - state.chargebeeEstimate.total

        // @todo - take care of this once we have different tax rates per item - PT
        ret.forEach((item) => {
          if (item.tax === undefined) { item.tax = Math.round(item.amount * (state.chargebeeEstimate.line_item_taxes[0].tax_rate / 100)) }

          pennyOff -= item.amount + item.tax
        })

        ret[ret.length - 1].tax += pennyOff
      }

      return ret
    },

    /**
     * Sum of all Fee rates.
     */
    getFee (state) {
      return state.fee
    },

    /**
     * If we have a coupon from Chargebee use that total (this includes Fees and discounts),
     * otherwise use the total from the API.
     */
    getMonthlyTotal (state, getters) {
      if (state.chargebeeEstimate.total >= 0) {
        return state.chargebeeEstimate.total / 100
      }

      return getters.getTotal + getters.getFee
    },

    /*
     * Legal Coupons
     */
    isCouponWithFreeLegalEntered: (state) => {
      if (!Array.isArray(state.couponId)) return false
      const allowedCoupons = ['UNDERPINNED', 'DISPACELEGAL']
      return state.couponId.length && state.couponId.some(c => allowedCoupons.includes(c))
    },
    isOnlyLegalExpensesSelected: (state, getters) => {
      return getters.getSelectedRatesIds.length === 1 && state.products[0].key === 'LegalExpenses'
    },
  },
})
